import React, { useEffect, useState } from 'react'
import { textColors } from '../../Common/Color/Color'
import { Box, Card, CardContent, Typography, Grid } from '@mui/material'
import StarsRoundedIcon from '@mui/icons-material/StarsRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import { fontSizes, fontWeights } from '../../Common/Typography/TypographyStyles'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { useFetchTeamMemberPoints } from '../../../hooks/teamMemberHooks/useFetchTeamMemberPoints'
import { TeamMemberPoints } from '../../../redux/teamMember/types'
import { selectUpcomingEventCount } from '../../../redux/event/manageEvents/selector'
import { selectPendingTaskCount } from '../../../redux/task/selector'
import { selectUserTeamMemberInfo } from '../../../redux/auth/selector'

interface CardData {
  key: string
  label: string
  counts: number
  background: string
  icon: React.ElementType
}

const OverviewCard = () => {
  const dispatch = useAppDispatch()
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)
  const upcomingEventCount = useAppSelector(selectUpcomingEventCount)
  const pendingTaskCount = useAppSelector(selectPendingTaskCount)

  const [cardData, setCardData] = useState<CardData[]>([
    {
      key: 'star-points',
      label: 'STAR POINTS',
      counts: 0,
      background: 'linear-gradient(247.63deg, #FFB5B5 14.41%, #CD9BFF 77.64%)',
      icon: StarsRoundedIcon,
    },
    {
      key: 'upcoming-events',
      label: 'UPCOMING EVENTS',
      counts: 0,
      background: 'linear-gradient(247.63deg, #C4FFB5 14.41%, #9BC9FF 77.64%)',
      icon: CalendarMonthRoundedIcon,
    },
    {
      key: 'pending-task',
      label: 'PENDING TASKS',
      counts: 0,
      background: 'linear-gradient(246.64deg, #F6D9AE 17.13%, #86DAF4 79.29%)',
      icon: FormatListBulletedRoundedIcon,
    },
    {
      key: 'cop',
      label: 'COP',
      counts: 0,
      background: 'linear-gradient(247.63deg, #FFEFB5 14.41%, #FF9B9B 77.64%)',
      icon: WorkspacePremiumRoundedIcon,
    },
  ])
  const teamMemberQuery = {
    teamMemberWorkdayId: teamMemberDetails.workdayId,
    teamMemberEmail: teamMemberDetails.email,
  }

  const { teamMemberPoints } = useFetchTeamMemberPoints(teamMemberQuery)

  useEffect(() => {
    const fetchData = async () => {
      let totalStarPoints = 0
      let totalCopPoints = 0

      teamMemberPoints.forEach((teamMemberPoint: TeamMemberPoints) => {
        totalStarPoints += teamMemberPoint.starPoints - teamMemberPoint.starsDeduction || 0
        totalCopPoints += teamMemberPoint.copPoints - teamMemberPoint.copDeduction || 0
      })

      const counts: Record<string, number> = {
        'star-points': totalStarPoints,
        'upcoming-events': upcomingEventCount,
        'pending-task': pendingTaskCount,
        cop: totalCopPoints,
      }

      const updatedCardData = cardData.map((card) => ({
        ...card,
        counts: counts[card.key] || card.counts,
      }))

      setCardData(updatedCardData)
    }

    fetchData()
  }, [dispatch, upcomingEventCount, pendingTaskCount, teamMemberPoints])

  return (
    <React.Fragment>
      <Grid container spacing={2}>
        {cardData.map(({ key, label, counts, background, icon: Icon }) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
            <Card
              id={key}
              key={key}
              sx={{
                background: background,
                color: textColors.white,
                borderRadius: fontSizes.size12,
                padding: 0,
              }}
            >
              <CardContent>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                  <Typography
                    sx={{
                      fontSize: { xs: fontSizes.size12, md: fontSizes.size16 },
                      fontWeight: fontWeights[500],
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    {label}
                  </Typography>

                  <Icon
                    sx={{
                      fontSize: {
                        xs: fontSizes.size28,
                        md: fontSizes.size36,
                      },
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: fontSizes.size16, md: fontSizes.size32 },
                    fontWeight: fontWeights[500],
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  }}
                >
                  {counts}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </React.Fragment>
  )
}

export default OverviewCard
